import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from "../../layout/LayoutHeader";
import { postDataAPI, getDataAPI } from "../../utils/fetchData";
import axios from 'axios';
import { BASE_URL } from "../../utils/axios";
import usageTracker from "../../utils/usageTracker";

const IndividualImport = () => {
    const [step, setStep] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [trackDetails, setTrackDetails] = useState(null);
    
    const token = localStorage.getItem('dizeeToken');
    const navigate = useNavigate();

    const searchTracks = async () => {
        if (!searchQuery.trim()) {
            alert('Please enter a song title or artist name to search');
            return;
        }

        setIsSearching(true);
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            console.log('Searching for tracks:', searchQuery);
            
            const response = await postDataAPI("user/searchTracksForImport", { 
                query: searchQuery
            }, token);

            console.log('Track search response:', response.data);

            if (response.data?.status === 200) {
                const tracks = response.data?.data?.tracks?.items || [];
                setSearchResults(tracks);
                setStep(1); // Move to results step
            } else {
                alert(response.data?.message || "No tracks found. Please try a different search term.");
                setSearchResults([]);
            }
        } catch (error) {
            console.error("Error searching tracks:", error);
            alert("Error searching for tracks. Please try again.");
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const selectTrack = (track) => {
        console.log('Selected track:', track);
        setSelectedTrack(track);
        
        // Extract and format track details
        const details = {
            song_title: track.name,
            artist_name: track.artists?.[0]?.name || 'Unknown Artist',
            album_name: track.album?.name || '',
            duration: Math.floor(track.duration_ms / 1000), // Convert to seconds
            release_date: track.album?.release_date || '',
            spotify_id: track.id,
            spotify_url: track.external_urls?.spotify || '',
            preview_url: track.preview_url || '',
            album_artwork: track.album?.images?.[0]?.url || '',
            popularity: track.popularity || 0,
            explicit: track.explicit || false,
            isrc: track.external_ids?.isrc || '',
            track_number: track.track_number || 1,
            disc_number: track.disc_number || 1,
            artist_id: track.artists?.[0]?.id || '',
            album_id: track.album?.id || '',
            album_type: track.album?.album_type || '',
            featured_artists: track.artists?.slice(1).map(artist => artist.name).join(', ') || '',
            all_artists: track.artists?.map(artist => artist.name).join(', ') || '',
            genre: '', // Will be filled from album/artist info if available
            label: '', // Will be filled from additional API call if needed
            status: 'released', // Default status for Spotify tracks
        };
        
        setTrackDetails(details);
        setStep(2); // Move to confirmation step
    };

    const importTrack = async () => {
        if (!trackDetails) {
            alert('No track selected for import');
            return;
        }

        setIsImporting(true);
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            
            // Step 1: Get existing catalog to check for duplicates
            console.log('📚 IndividualImport: Fetching existing catalog to check for duplicates...');
            let existingCatalog = [];
            try {
                const catalogResponse = await getDataAPI('user/getCatalog', token);
                if (catalogResponse?.data?.status === 200 && catalogResponse?.data?.data) {
                    existingCatalog = Array.isArray(catalogResponse.data.data) ? catalogResponse.data.data : catalogResponse.data.data.data;
                    console.log('📚 IndividualImport: Found', existingCatalog?.length || 0, 'existing tracks in catalog');
                }
            } catch (catalogError) {
                console.warn('⚠️ IndividualImport: Could not fetch existing catalog, proceeding without duplicate check:', catalogError);
            }
            
            // Step 2: Check for duplicates
            if (existingCatalog.length > 0) {
                const isDuplicate = existingCatalog.some(existingTrack => {
                    // Normalize strings for comparison (lowercase, trim whitespace)
                    const normalize = (str) => str ? str.toLowerCase().trim() : '';
                    
                    const newSongName = normalize(trackDetails.song_name || trackDetails.songName || trackDetails.title);
                    const newArtistName = normalize(trackDetails.artist_name || trackDetails.artistName || trackDetails.artist);
                    const newISRC = normalize(trackDetails.isrc || trackDetails.ISRC);
                    
                    const existingSongName = normalize(existingTrack.song_name || existingTrack.songName || existingTrack.title);
                    const existingArtistName = normalize(existingTrack.artist_name || existingTrack.artistName || existingTrack.artist);
                    const existingISRC = normalize(existingTrack.isrc || existingTrack.ISRC);
                    
                    // Check for duplicate based on multiple criteria
                    const sameSongAndArtist = newSongName && newArtistName && 
                                            newSongName === existingSongName && 
                                            newArtistName === existingArtistName;
                    
                    const sameISRC = newISRC && existingISRC && newISRC === existingISRC;
                    
                    // Consider it a duplicate if:
                    // 1. Same ISRC (most reliable identifier)
                    // 2. Same song name AND artist name
                    return sameISRC || sameSongAndArtist;
                });
                
                if (isDuplicate) {
                    const trackName = trackDetails.song_name || trackDetails.title || 'Unknown Track';
                    const artistName = trackDetails.artist_name || trackDetails.artist || 'Unknown Artist';
                    
                    alert(`This track "${trackName}" by ${artistName} already exists in your catalog and won't be imported again.`);
                    setIsImporting(false);
                    return;
                }
            }
            
            // Step 3: Import the track if it's not a duplicate
            const response = await postDataAPI(
                'user/saveCatalog',
                { data: [trackDetails] },
                token
            );
            
            if (response.data.status === 200) {
                alert('Track imported successfully to your catalog!');
                
                // Track catalog usage for individual import
                try {
                    await usageTracker.trackCatalogEntry(1, 'individual_import');
                } catch (trackingError) {
                    console.error('Usage tracking failed:', trackingError);
                }
                
                navigate('/catalog/view-all');
            } else {
                console.log('Import response not successful:', response.data);
                alert('Error importing track. Please try again.');
            }
        } catch (error) {
            console.error('Error importing track:', error);
            alert('Error importing track. Please try again.');
        } finally {
            setIsImporting(false);
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds) return 'Unknown';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            searchTracks();
        }
    };

    const goBack = () => {
        if (step === 0) {
            navigate('/catalog');
        } else {
            setStep(step - 1);
        }
    };

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-full max-w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>
                            {step === 0 && 'Import Song'}
                            {step === 1 && 'Search Results'}
                            {step === 2 && 'Confirm Import'}
                        </span>
                        <div className="flex items-center gap-4">
                            {/* Navigation Buttons */}
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate('/catalog/view-all')}
                            >
                                View
                            </span>
                            
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate('/catalog/manual-add')}
                            >
                                Manual Add
                            </span>
                            
                        <span 
                            className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                            onClick={goBack}
                        >
                            Go back
                        </span>
                        </div>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        {/* Step 0: Search Input */}
                        {step === 0 && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] text-left">
                                        Enter a song title, artist name, or both to find the track you want to import
                                    </p>
                                </div>

                                <div className="mb-6">
                                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                        <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                            Search Query
                                        </label>
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder="e.g., 'Blinding Lights The Weeknd' or 'Bad Bunny'"
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={searchTracks}
                                    disabled={isSearching || !searchQuery.trim()}
                                    className="w-full bg-[#17A2B8] text-white py-3 rounded-lg text-[12px] font-semibold hover:bg-[#20B2AA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSearching ? 'Searching...' : 'Search Songs'}
                                </button>

                                <div className="mt-6 text-[10px] text-[rgba(255,255,255,0.5)] leading-[1.5]">
                                    <p>• Search by artist: "The Weeknd"</p>
                                    <p>• Search by both: "Blinding Lights The Weeknd"</p>
                                    <p>• Results are powered by Spotify's database</p>
                                </div>
                            </div>
                        )}

                        {/* Step 1: Search Results */}
                        {step === 1 && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] text-left">
                                        {searchResults.length} Search results for "{searchQuery}". Click on a song to import it.
                                    </p>
                                </div>

                                {searchResults.length === 0 ? (
                                    <div className="text-center py-[60px]">
                                        <h3 className="text-white text-[12px] font-semibold mb-2">
                                            No tracks found
                                        </h3>
                                        <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px] mx-auto">
                                            Try different search terms or check your spelling.
                                        </p>
                                        <button 
                                            onClick={() => setStep(0)}
                                            className="bg-[#17A2B8] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#20B2AA] transition-colors"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid gap-4">
                                        {searchResults.map((track, index) => (
                                            <div 
                                                key={track.id || index}
                                                onClick={() => selectTrack(track)}
                                                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all cursor-pointer w-full overflow-hidden"
                                            >
                                                <div className="flex items-start gap-3 w-full">
                                                    {/* Album Art */}
                                                    <div className="w-10 h-10 bg-[rgba(23,162,184,0.2)] rounded-lg flex items-center justify-center flex-shrink-0">
                                                        {track.album?.images?.[0]?.url ? (
                                                            <img 
                                                                src={track.album.images[0].url} 
                                                                alt="Album Art" 
                                                                className="w-full h-full object-cover rounded-lg"
                                                            />
                                                        ) : (
                                                            <div className="w-5 h-5 bg-[#17A2B8] rounded"></div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Track Info */}
                                                    <div className="flex-1 min-w-0 overflow-hidden">
                                                        <h3 className="text-white text-[12px] font-normal truncate mb-1 w-full">
                                                            {track.name}
                                                        </h3>
                                                        <p className="text-[rgba(255,255,255,0.7)] text-[10px] truncate mb-2 w-full">
                                                            {track.artists?.map(artist => artist.name).join(', ') || 'Unknown Artist'}
                                                        </p>
                                                        <div className="flex items-center gap-1 text-[rgba(255,255,255,0.5)] text-[9px] flex-wrap w-full">
                                                            {track.album?.name && (
                                                                <span className="bg-[rgba(23,162,184,0.2)] text-[rgba(23,162,184,1)] px-1.5 py-0.5 rounded text-[8px] truncate max-w-[120px]" title={track.album.name}>
                                                                    {track.album.name}
                                                                </span>
                                                            )}
                                                            {track.duration_ms && (
                                                                <span className="bg-[rgba(255,255,255,0.1)] px-1.5 py-0.5 rounded text-[8px] whitespace-nowrap">
                                                                    {formatDuration(Math.floor(track.duration_ms / 1000))}
                                                                </span>
                                                            )}
                                                            {track.album?.release_date && (
                                                                <span className="bg-[rgba(255,255,255,0.1)] px-1.5 py-0.5 rounded text-[8px] whitespace-nowrap">
                                                                    {new Date(track.album.release_date).getFullYear()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 2: Confirm Import */}
                        {step === 2 && trackDetails && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] text-left">
                                        Review the track details before importing to your catalog
                                    </p>
                                </div>

                                {/* Track Preview */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-6">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-16 h-16 bg-[rgba(23,162,184,0.2)] rounded-lg flex items-center justify-center flex-shrink-0">
                                            {trackDetails.album_artwork ? (
                                                <img 
                                                    src={trackDetails.album_artwork} 
                                                    alt="Album Art" 
                                                    className="w-full h-full object-cover rounded-lg"
                                                />
                                            ) : (
                                                <div className="w-8 h-8 bg-[#17A2B8] rounded"></div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-white text-[14px] font-semibold mb-1">
                                                {trackDetails.song_title}
                                            </h3>
                                            <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-1">
                                                {trackDetails.artist_name}
                                            </p>
                                            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                                {trackDetails.album_name}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Track Details */}
                                    <div className="grid grid-cols-2 gap-3 text-[10px]">
                                        <div>
                                            <span className="text-[rgba(255,255,255,0.5)]">Duration:</span>
                                            <p className="text-white">{formatDuration(trackDetails.duration)}</p>
                                        </div>
                                        <div>
                                            <span className="text-[rgba(255,255,255,0.5)]">Release Date:</span>
                                            <p className="text-white">{trackDetails.release_date || 'Unknown'}</p>
                                        </div>
                                        <div>
                                            <span className="text-[rgba(255,255,255,0.5)]">Track #:</span>
                                            <p className="text-white">{trackDetails.track_number}</p>
                                        </div>
                                        <div>
                                            <span className="text-[rgba(255,255,255,0.5)]">Explicit:</span>
                                            <p className="text-white">{trackDetails.explicit ? 'Yes' : 'No'}</p>
                                        </div>
                                        {trackDetails.isrc && (
                                            <>
                                                <div className="col-span-2">
                                                    <span className="text-[rgba(255,255,255,0.5)]">ISRC:</span>
                                                    <p className="text-white">{trackDetails.isrc}</p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Import Button */}
                                <button
                                    onClick={importTrack}
                                    disabled={isImporting}
                                    className="w-full bg-[#17A2B8] text-white py-3 rounded-lg text-[12px] font-semibold hover:bg-[#20B2AA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                                >
                                    {isImporting ? 'Importing...' : 'Import to Catalog'}
                                </button>

                                <div className="text-[10px] text-[rgba(255,255,255,0.5)] leading-[1.5]">
                                    <p>• This track will be added to your catalog with "released" status</p>
                                    <p>• You can edit the details later from the catalog view</p>
                                    <p>• Spotify metadata will be preserved for reference</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default IndividualImport; 